import type { TLRecord } from "@tldraw/tlschema";
import { isEqual } from "@tldraw/utils";
import { plainText, type AgentSnapshot } from "./agent-board";
import type { AgentRunInfo } from "./agent-runtime";
import { compactRecords } from "./agent-context";
import type { SceneSpec } from "./scene-spec";

export type DraftRecords = Record<string, TLRecord>;
export type DraftChange = { before: TLRecord | null; after: TLRecord | null };
export type DraftStep = {
  kind?: "context" | "result";
  id: string;
  title: string;
  at: number;
  sourceIds: string[];
  changes: Record<string, DraftChange>;
};
export type DraftSource = {
  key: string;
  id: string;
  label: string;
  at: number;
  record: TLRecord;
};
export type AgentResource = {
  id: string;
  kind:
    | "web_page"
    | "web_search"
    | "web_image"
    | "generated_image"
    | "canvas_image"
    | "scene";
  title: string;
  at: number;
  url?: string;
  pageUrl?: string;
  author?: string;
  licence?: string;
  prompt?: string;
  sourceIds?: string[];
  src?: string;
  w?: number;
  h?: number;
  mimeType?: string;
  provider?: string;
  project?: string;
  excerpt?: string;
  contentDigest?: string;
  /** A declarative 3D scene built by build_scene; rendered in the browser. */
  /** The board shape it was placed as, when it was placed. */
  shapeId?: string;
  scene?: SceneSpec;
};
export type AgentToolCall = {
  id: string;
  name: string;
  at: number;
  finished?: number;
  state: "running" | "done" | "failed";
  input: unknown;
  error?: string;
};
export type DraftStatus =
  "new" | "working" | "waiting" | "ready" | "stopped" | "failed" | "applied";
export type AgentDraft = {
  id: string;
  roomId: string;
  userId: string;
  title: string;
  status: DraftStatus;
  created: number;
  updated: number;
  version: number;
  baseRevision: number;
  scopeIds: string[];
  contextLabel: string;
  /** Which of the eight agents runs this conversation; the user's chosen style draws it. */
  agent?: number;
  /** The comment thread this conversation was called from with @Agent; replies go there. */
  threadId?: string;
  /** @Agent occurrence and answer-comment IDs that already ran, so a repeat or an edit never reruns. */
  handled?: string[];
  archivedAt?: number | null;
  deletedAt?: number;
  base: DraftRecords;
  head: DraftRecords;
  steps: DraftStep[];
  sources: DraftSource[];
  messages: {
    role: "user" | "agent";
    text: string;
    at: number;
    /** Existing objects outside the editable set the agent asked to change. */
    editIds?: string[];
    /** Set once the user allowed those changes. */
    allowed?: boolean;
  }[];
  error: string;
  runId: string | null;
  appliedBatchId: string | null;
  turnBase?: DraftRecords;
  applications?: {
    batchId: string;
    /** The thread reply that reports this action. */
    replyId?: string;
    undoBatchId?: string;
    redoBatchId?: string;
    at: number;
  }[];
  historyCursor?: number;
  resources?: AgentResource[];
  toolCalls?: AgentToolCall[];
  runs?: AgentRunInfo[];
};
export type DraftSummary = Pick<
  AgentDraft,
  | "id"
  | "title"
  | "status"
  | "created"
  | "updated"
  | "version"
  | "scopeIds"
  | "contextLabel"
  | "agent"
  | "threadId"
  | "archivedAt"
  | "deletedAt"
>;

export const AGENT_SLOTS = 8;
/** A conversation's agent, 0 to 7. Conversations created before agents had one derive it from their ID. */
export function agentSlot(d: { id: string; agent?: number | null }) {
  if (Number.isInteger(d.agent) && d.agent! >= 0 && d.agent! < AGENT_SLOTS)
    return d.agent!;
  let hash = 0;
  for (const c of d.id) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  return hash % AGENT_SLOTS;
}
/** The least used agent that is not busy in another conversation, so concurrent work never shares a face. */
export function nextAgentSlot(
  others: { id: string; agent?: number | null; status: string }[],
) {
  const counts = Array<number>(AGENT_SLOTS).fill(0);
  const busy = new Set<number>();
  for (const other of others) {
    const slot = agentSlot(other);
    counts[slot]++;
    if (other.status === "working" || other.status === "waiting")
      busy.add(slot);
  }
  const all = counts.map((_, i) => i);
  const free = all.filter((i) => !busy.has(i));
  return (free.length ? free : all).sort(
    (a, b) => counts[a] - counts[b] || a - b,
  )[0];
}
export type DraftConflict = {
  key: string;
  recordId: string;
  label: string;
  path: string[];
  base: unknown;
  current: unknown;
  proposed: unknown;
};
export type DraftComparison = {
  revision: number;
  conflicts: DraftConflict[];
  staleSourceIds: string[];
  changes: Record<string, DraftChange>;
};
export type DraftResolutions = Record<string, "current" | "draft">;
const object = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

export function recordLabel(record: TLRecord): string {
  if (record.typeName === "shape") {
    if (
      "name" in record.props &&
      typeof record.props.name === "string" &&
      record.props.name
    )
      return record.props.name;
    if ("richText" in record.props)
      return plainText(record.props.richText).slice(0, 90) || record.type;
    if (record.type === "drafting" || record.type === "mark")
      return record.props.kind;
    return record.type;
  }
  if (record.typeName === "comment")
    return plainText(record.body).slice(0, 90) || "Comment";
  return record.typeName;
}

export function draftScope(state: AgentSnapshot, selected: string[]) {
  if (selected.length > 100) throw new Error("Select up to 100 objects.");
  const ids = new Set(selected);
  for (const id of ids)
    if (state.store[id]?.typeName !== "shape")
      throw new Error("The selection changed. Select it again.");
  // Nothing selected: the agent may read everything but edit only what it creates.
  return withChildren(state.store, ids);
}
function withChildren(store: DraftRecords, ids: Set<string>) {
  for (let depth = 0; depth < 32; depth++) {
    const size = ids.size;
    for (const r of Object.values(store))
      if (r.typeName === "shape" && ids.has(r.parentId)) ids.add(r.id);
    if (size === ids.size) break;
  }
  return [...ids];
}

/**
 * The existing objects a conversation may change at the start of a turn: its
 * focus and objects the user allowed, if they still exist, and what it created,
 * each with their children.
 */
export function refreshScope(draft: AgentDraft, live: DraftRecords) {
  const ids = new Set(
    draft.scopeIds.filter((id) => live[id]?.typeName === "shape"),
  );
  for (const r of Object.values(live))
    if (
      r.typeName === "shape" &&
      r.id.startsWith(`shape:d${draft.id.slice(0, 8)}_`)
    )
      ids.add(r.id);
  return withChildren(live, ids);
}

/** Only what the agent asked about in its latest reply can be allowed, once. */
export function allowEdits(draft: AgentDraft, live: DraftRecords) {
  const last = draft.messages.at(-1);
  if (draft.status === "working")
    throw new Error("Stop the active task first.");
  if (last?.role !== "agent" || !last.editIds?.length || last.allowed)
    throw new Error("There is no pending request to allow.");
  const ids = new Set(draft.scopeIds);
  for (const id of last.editIds)
    if (live[id]?.typeName === "shape") ids.add(id);
  draft.scopeIds = withChildren(live, ids);
  last.allowed = true;
  return draft;
}

/** Keep requested edit IDs that name real objects the agent may not change yet. */
export function requestedEdits(draft: AgentDraft, ids: string[] | undefined) {
  const scope = new Set(draft.scopeIds);
  return [...new Set(ids ?? [])]
    .filter((id) => draft.head[id]?.typeName === "shape" && !scope.has(id))
    .slice(0, 20);
}

/** Supply the selected content, its ancestors/assets and relevant discussions. */
export function draftContext(draft: AgentDraft): TLRecord[] {
  const baseline = draft.turnBase ?? draft.base;
  const ids = new Set(draft.scopeIds);
  // Pages are where new sheets can go; the map shows everything else.
  for (const r of Object.values(draft.head))
    if (r.typeName === "page") ids.add(r.id);
  for (const [id, record] of Object.entries(draft.head))
    if (!baseline[id] && record.typeName === "shape") ids.add(id);
  // A thread conversation reads its own discussion and may add to its page.
  const thread = draft.threadId ? draft.head[draft.threadId] : undefined;
  if (thread?.typeName === "comment-thread") {
    ids.add(thread.id);
    ids.add(thread.pageId);
  }
  for (let depth = 0; depth < 32; depth++) {
    const size = ids.size;
    for (const id of [...ids]) {
      const r = draft.head[id];
      if (r?.typeName === "shape") {
        ids.add(r.parentId);
        if ("assetId" in r.props && r.props.assetId) ids.add(r.props.assetId);
      }
    }
    if (size === ids.size) break;
  }
  for (const r of Object.values(draft.head)) {
    if (
      r.typeName === "comment-thread" &&
      r.anchor.type === "shape" &&
      ids.has(r.anchor.shapeId)
    )
      ids.add(r.id);
    if (r.typeName === "binding" && ids.has(r.fromId) && ids.has(r.toId))
      ids.add(r.id);
  }
  for (const r of Object.values(draft.head))
    if (r.typeName === "comment" && ids.has(r.threadId)) ids.add(r.id);
  const records = [...ids].flatMap((id) =>
    draft.head[id] ? [draft.head[id]] : [],
  );
  // Measure what the model is sent, not the stored records.
  if (JSON.stringify(compactRecords(records, draft.head)).length > 400_000)
    throw new Error(
      "This context is too large. Start a conversation with a smaller selection.",
    );
  return records;
}

export function recordChanges(before: DraftRecords, after: DraftRecords) {
  const changes: Record<string, DraftChange> = {};
  for (const id of new Set([...Object.keys(before), ...Object.keys(after)]))
    if (!isEqual(before[id], after[id]))
      changes[id] = { before: before[id] ?? null, after: after[id] ?? null };
  return changes;
}

export function replayDraft(draft: AgentDraft, count: number): DraftRecords {
  const records = { ...draft.base };
  for (const step of draft.steps.slice(0, count))
    for (const [id, change] of Object.entries(step.changes)) {
      if (change.after) records[id] = change.after;
      else delete records[id];
    }
  return records;
}

/** Three-way field merge. Arrays are atomic; every unresolved conflict keeps main. */
export function compareDraft(
  draft: AgentDraft,
  current: AgentSnapshot,
  resolutions: DraftResolutions = {},
): DraftComparison {
  const baseline = draft.turnBase ?? draft.base;
  const conflicts: DraftConflict[] = [];
  const result = { ...current.store };
  function merge(
    id: string,
    path: string[],
    base: unknown,
    live: unknown,
    proposed: unknown,
  ): unknown {
    if (isEqual(base, proposed)) return live;
    if (isEqual(base, live) || isEqual(live, proposed)) return proposed;
    if (object(base) && object(live) && object(proposed)) {
      const out: Record<string, unknown> = {};
      for (const key of new Set([
        ...Object.keys(base),
        ...Object.keys(live),
        ...Object.keys(proposed),
      ])) {
        const value = merge(
          id,
          [...path, key],
          base[key],
          live[key],
          proposed[key],
        );
        if (value !== undefined)
          Object.defineProperty(out, key, {
            value,
            enumerable: true,
            configurable: true,
            writable: true,
          });
      }
      return out;
    }
    const key = JSON.stringify([id, ...path]);
    if (resolutions[key] === "draft") return proposed;
    if (resolutions[key] === "current") return live;
    const r = draft.head[id] ?? draft.base[id];
    conflicts.push({
      key,
      recordId: id,
      label: recordLabel(r),
      path,
      base,
      current: live,
      proposed,
    });
    return live;
  }
  for (const id of Object.keys(recordChanges(baseline, draft.head))) {
    const merged = merge(
      id,
      [],
      baseline[id],
      current.store[id],
      draft.head[id],
    ) as TLRecord | undefined;
    if (merged) result[id] = merged;
    else delete result[id];
  }
  const staleSourceIds = [
    ...new Set(
      draft.sources
        .filter(
          (s) =>
            baseline[s.id] && !isEqual(baseline[s.id], current.store[s.id]),
        )
        .map((s) => s.id),
    ),
  ];
  return {
    revision: current.revision,
    conflicts,
    staleSourceIds,
    changes: recordChanges(current.store, result),
  };
}
